import type { ComponentType } from 'react';
import type { Conditions } from '@atlassian/forge-conditions';
import type { FieldKey } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/fields/types.tsx';
import type {
	FieldCategory,
	FieldOperations,
} from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	ASSETS_CF_TYPE,
	ATTACHMENT_TYPE as ATTACHMENT_FIELD_TYPE,
	CASCADING_SELECT_CF_TYPE,
	DATE_CF_TYPE,
	DATETIME_CF_TYPE,
	FLAGGED_CF_TYPE,
	GROUP_CF_TYPE,
	LABELS_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	MULTI_USER_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	NUMBER_CF_TYPE,
	PARENT_CF_TYPE,
	PEOPLE_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	SELECT_CF_TYPE,
	SERVICE_ENTITY_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	SUMMARY_TYPE as SUMMARY_FIELD_TYPE,
	TEAM_CF_TYPE,
	TEXT_CF_TYPE,
	URL_CF_TYPE,
	USER_CF_TYPE,
	VERSION_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	BASELINE_END_CF_TYPE,
	BASELINE_START_CF_TYPE,
	DESCRIPTION_FIELD_TYPE,
	ENTITLEMENT_CF_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	ORGANIZATION_CF_TYPE,
	REPORTER_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import type { FieldUuid, OptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import type { FormatConfigurationType } from '@atlassian/jira-platform-utils-number/src/ui/number-field-configuration/types.tsx';
import type { Group } from '../constants.tsx';
import type { EmptyStateComponentProps } from './empty-state.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type {
	FieldCategory as FieldCategoryId,
	FieldOperations,
} from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types';
export type FieldType =
	| typeof SUMMARY_FIELD_TYPE
	| typeof DESCRIPTION_FIELD_TYPE
	| typeof ATTACHMENT_FIELD_TYPE
	| typeof NUMBER_CF_TYPE
	| typeof STORY_POINT_ESTIMATE_CF_TYPE
	| typeof URL_CF_TYPE
	| typeof TEXT_CF_TYPE
	| typeof TEXT_AREA_CF_TYPE
	| typeof LABELS_CF_TYPE
	| typeof DATE_CF_TYPE
	| typeof DATETIME_CF_TYPE
	| typeof RADIO_BUTTONS_CF_TYPE
	| typeof MULTI_CHECKBOXES_CF_TYPE
	| typeof SELECT_CF_TYPE
	| typeof MULTI_VERSION_CF_TYPE
	| typeof VERSION_CF_TYPE
	| typeof GROUP_CF_TYPE
	| typeof MULTI_GROUP_CF_TYPE
	| typeof USER_CF_TYPE
	| typeof MULTI_USER_CF_TYPE
	| typeof FLAGGED_CF_TYPE
	| typeof TEAM_CF_TYPE
	| typeof PARENT_CF_TYPE
	| typeof BASELINE_START_CF_TYPE
	| typeof BASELINE_END_CF_TYPE
	| typeof ASSETS_CF_TYPE
	| typeof SERVICE_ENTITY_CF_TYPE
	| typeof PEOPLE_CF_TYPE
	| typeof CASCADING_SELECT_CF_TYPE
	| typeof ENVIRONMENT_FIELD_TYPE
	| typeof ENTITLEMENT_CF_TYPE
	| typeof ORGANIZATION_CF_TYPE
	| typeof REPORTER_FIELD_TYPE;

const fieldTypesMap: {
	[fieldType: string]: FieldType | undefined;
} = {
	[SUMMARY_FIELD_TYPE]: SUMMARY_FIELD_TYPE,
	[DESCRIPTION_FIELD_TYPE]: DESCRIPTION_FIELD_TYPE,
	[ATTACHMENT_FIELD_TYPE]: ATTACHMENT_FIELD_TYPE,
	[NUMBER_CF_TYPE]: NUMBER_CF_TYPE,
	[STORY_POINT_ESTIMATE_CF_TYPE]: STORY_POINT_ESTIMATE_CF_TYPE,
	[URL_CF_TYPE]: URL_CF_TYPE,
	[TEXT_CF_TYPE]: TEXT_CF_TYPE,
	[TEXT_AREA_CF_TYPE]: TEXT_AREA_CF_TYPE,
	[LABELS_CF_TYPE]: LABELS_CF_TYPE,
	[DATE_CF_TYPE]: DATE_CF_TYPE,
	[DATETIME_CF_TYPE]: DATETIME_CF_TYPE,
	[RADIO_BUTTONS_CF_TYPE]: RADIO_BUTTONS_CF_TYPE,
	[MULTI_CHECKBOXES_CF_TYPE]: MULTI_CHECKBOXES_CF_TYPE,
	[SELECT_CF_TYPE]: SELECT_CF_TYPE,
	[MULTI_VERSION_CF_TYPE]: MULTI_VERSION_CF_TYPE,
	[VERSION_CF_TYPE]: VERSION_CF_TYPE,
	[GROUP_CF_TYPE]: GROUP_CF_TYPE,
	[MULTI_GROUP_CF_TYPE]: MULTI_GROUP_CF_TYPE,
	[USER_CF_TYPE]: USER_CF_TYPE,
	[MULTI_USER_CF_TYPE]: MULTI_USER_CF_TYPE,
	[FLAGGED_CF_TYPE]: FLAGGED_CF_TYPE,
	[TEAM_CF_TYPE]: TEAM_CF_TYPE,
	[PARENT_CF_TYPE]: PARENT_CF_TYPE,
	[BASELINE_START_CF_TYPE]: BASELINE_START_CF_TYPE,
	[BASELINE_END_CF_TYPE]: BASELINE_END_CF_TYPE,
	[ASSETS_CF_TYPE]: ASSETS_CF_TYPE,
	[SERVICE_ENTITY_CF_TYPE]: SERVICE_ENTITY_CF_TYPE,
	[PEOPLE_CF_TYPE]: PEOPLE_CF_TYPE,
	[CASCADING_SELECT_CF_TYPE]: CASCADING_SELECT_CF_TYPE,
	[ENVIRONMENT_FIELD_TYPE]: ENVIRONMENT_FIELD_TYPE,
	[ENTITLEMENT_CF_TYPE]: ENTITLEMENT_CF_TYPE,
	[ORGANIZATION_CF_TYPE]: ORGANIZATION_CF_TYPE,
	[REPORTER_FIELD_TYPE]: REPORTER_FIELD_TYPE,
};

export const toFieldType = (strFieldType: string): FieldType => {
	const fieldType = fieldTypesMap[strFieldType];
	if (!fieldType) {
		throw new Error(`Failed to convert string to FieldType. Invalid field type: ${strFieldType}`);
	}

	return fieldType;
};

export type WhatIsThisConfiguration = {
	buttonLabel: MessageDescriptor;
	content: MessageDescriptor;
	linkToDocumentationText: MessageDescriptor;
	/** article id link for in product help.
	 * It will use the in product help component HelpPanelLink if you provide an article id
	 * otherwise it will use an <a> element and open the link in the new browser tab when click
	 */
	linkToInProductHelpArticleId?: string;
};

export const PANEL_LAYOUT_ITEM_TYPE = 'PANEL' as const;
export const FIELD_LAYOUT_ITEM_TYPE = 'FIELD' as const;

export type LayoutItemType = typeof PANEL_LAYOUT_ITEM_TYPE | typeof FIELD_LAYOUT_ITEM_TYPE;

export type LayoutItemFilter = string;

export const allowFieldLayoutItem = (type: '*' | FieldType): LayoutItemFilter =>
	`${FIELD_LAYOUT_ITEM_TYPE}:${type}`;

export const allowPanelLayoutItem = (panelId: '*' | string): LayoutItemFilter =>
	`${PANEL_LAYOUT_ITEM_TYPE}:${panelId}`;

// Option type that is used inside of the field, f.in. field can have options if it need to,
// id can be null for new options created on the frontend
// optimisticId is a unique id that should be used for rendering options, DnD, etc... For options returned from the API
// this will be option id as a string. For newly created options it will be a generated unique value, prefixed to avoid
// collision with existing options.
// see: src/packages/project-settings-apps/issue-type-page/src/view/field/field-options
export type OptionType = {
	readonly optimisticId: string;
	readonly value: string;
	readonly id: number | null;
	readonly parentId?: number | null;
	readonly parentExternalUuid?: null | OptionUuid;
	readonly externalUuid?: OptionUuid;
	readonly colour?: string | null | undefined;
};

export type FieldOptions = ReadonlyArray<OptionType>;

export type FieldProperties = Record<string, unknown> | null;

export type FieldConfiguration = {
	[key: string]: unknown;
	// Optional lozenge with tooltip shown when the field is in the inactive fields list
	// Data is populated by frontend during fetch and stripped before saving
	inactiveFieldLozenge?: {
		title: MessageDescriptor;
		tooltip?: MessageDescriptor;
		// Component id is used to identify the component,
		// eg. in analytics event on lozenge hover
		componentId?: string;
	};
};

export type FieldProvider = {
	key: string;
	name: string;
};

export type LayoutItemOperations = Readonly<{
	editable: boolean;
	removable: boolean;
	categoriesWhitelist: FieldCategory[];
	canAssociateInSettings: boolean;
	deletable: boolean;
}>;

export type FieldLayoutItemOperations = Readonly<
	LayoutItemOperations & {
		canModifyOptions: boolean;
		canModifyDefaultValue: boolean;
		canModifyPropertyConfiguration: boolean;
		canModifyRequired: boolean;
	}
>;

export type HiddenInConfiguration = {
	readonly id: string;
	readonly name: string;
};

export type HiddenInConfigurationWithIssueTypes = {
	readonly fieldConfigId: string;
	readonly fieldConfigName: string;
	/**
	 * Associated issue types of the field config scheme
	 */
	readonly fieldConfigIssueTypes: string[];
	/**
	 * This is not the actual "default field configuration". If true, this
	 * is the field configuration associated with all unmapped issue types.
	 * If true, fieldConfigIssueTypes will be an array of 'unknown' string
	 * representing each unknown issue type associated with the scheme for
	 * unmapped issue types
	 */
	isDefaultFieldConfig: boolean;
};

export type HiddenIn = {
	readonly layoutOwnerId?: string;
	readonly fieldConfigs: HiddenInConfiguration[];
};

export type AvailabilityContext = {
	readonly id: string | null;
	readonly issueTypeIds: ReadonlyArray<string>;
};

export type DisplayConditions = Conditions & {
	isSimpleIssueTypeDisplayCondition: boolean;
	isSimpleProjectDisplayCondition: boolean;
};

export type Availability = {
	readonly hiddenIn: HiddenIn[] | null;
	readonly context: AvailabilityContext | null;
	/**
	 *
	 * Forge apps allow to set display conditions on its modules (including
	 * custom fields). Using display conditions, lets can control the visibility
	 * of app modules in the UI.
	 *
	 * Forge is an app development platform, read about it here:
	 * @see https://developer.atlassian.com/platform/forge/
	 *
	 * You can read more about forge display conditions and see examples here:
	 * @see https://developer.atlassian.com/platform/forge/manifest-reference/display-conditions/
	 *
	 * Here is internal document with a QA demo scenario and sample display conditions,
	 * and expected outcome:
	 * @see https://hello.atlassian.net/wiki/spaces/~661111840/pages/3179864495/Admin+issue+layout+experiences+for+DC+AN
	 *
	 * We use these display conditions to show a warning panel explaining
	 * to users why their field is hidden from issue view.
	 */
	readonly displayConditions: DisplayConditions | null;
	/**
	 * Boolean value saying whether the field is hidden by App Access Rules
	 * in the current project.
	 *
	 * Access Narrowing is a feature built for organization admins to better
	 * manage and control what data an app can access within their organization.
	 *
	 * You can read more about Access Narrowing here:
	 * @see https://hello.atlassian.net/wiki/spaces/ECOTRUST/pages/2566609293/Experience+at+a+Glance+-+App+Access+Rule
	 */
	readonly isHiddenByAppAccessRules: boolean | null;
};

// Similar to Field in '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/fields/types'
// it's different from that 'Field' because options of this field have `optimisticId`
export type Field = {
	// Human readable name. May contain PII
	name: string;
	// For system fields, this contains its name ('reporter', 'assignee', ...).
	// For custom fields, this is 'customfield_<randomnumber>'.
	// Unique in Project context
	// No PII
	key: FieldKey;
	externalUuid: FieldUuid;
	// type may be 'text' | 'checkbox' | 'date' ... data type of the field
	// no PII
	type: string;
	// Frontend doesn't use custom or global and neither should you.
	// All use cases have been replaced with field operations. If you have new use case,
	// consider adding new operation for it.
	// We still have to fetch both booleans because server needs it on create and update,
	// but other than that pls do not read them and do not use them.
	// true = custom, false = system field
	custom: boolean;
	// We only check this when custom = true
	// true = global custom field -> we treat the field as a system field
	global: boolean;
	description: string;
	readonly options?: FieldOptions;
	readonly configuration?: FieldConfiguration;
	operations: FieldLayoutItemOperations;
	// Provider of the field used to tell users where it came from. This may be the platform, product that created the
	// field or third-party app.
	provider?: FieldProvider;
	properties?: FieldProperties;
	// Null means the back-end resolver hasn't been implemented yet and we treat the field as not restricted i.e. available
	availability: Availability | null;
	// This is used to store the format configuration for number field
	formatConfiguration?: FormatConfigurationType | null;
};

type KeyRequired = {
	key: FieldKey;
	type?: string;
};

type TypeRequired = {
	type: string;
	key?: FieldKey;
};

type KeyOrTypeRequired = KeyRequired | TypeRequired;

// This type is used to manually override a field's properties by the FieldCategoryConfiguration
// This type has all the same properties as AssociatedField except that the only required properties
// are key or type so as to be able to match the fields that need to be overridden.
//
// The `configuration` property cannot be overrode since it is `read-only
export type OverriddenField = KeyOrTypeRequired & {
	name?: string;
	alternateName?: string;
	alternateDescription?: string;
	custom?: boolean;
	global?: boolean;
	description?: string;
	category?: FieldCategory;
	required?: boolean;
	options?: FieldOptions;
	operations?: FieldLayoutItemOperations;
	defaultValue?: unknown;
	// Frontend-only concepts:
	sortable?: boolean;
};

export type AssociatedField = {
	// Human readable name. May contain PII
	name: string;
	alternateName?: string;
	alternateDescription?: string;
	// For system fields, this contains its name ('reporter', 'assignee', ...).
	// For custom fields, this is 'customfield_<randomnumber>'.
	// Unique in Project context
	// No PII
	key: FieldKey;
	externalUuid: FieldUuid;
	// type may be 'text' | 'checkbox' | 'date' ... data type of the field
	// no PII
	type: string;
	// Frontend doesn't use custom or global and neither should you.
	// All use cases have been replaced with field operations. If you have new use case,
	// consider adding new operation for it.
	// We still have to fetch both booleans because server needs it on create and update,
	// but other than that pls do not read them and do not use them.
	// true = custom, false = system field
	custom: boolean;
	// We only check this when custom = true
	// true = global custom field -> we treat the field as a system field
	global: boolean;
	description: string;
	category: FieldCategory;
	required: boolean;
	options?: FieldOptions;
	operations: FieldLayoutItemOperations;
	configuration?: FieldConfiguration;
	defaultValue?: unknown;
	// Provider of the field used to tell users where it came from. This may be the platform, product that created the
	// field or third-party app.
	provider?: FieldProvider;
	properties?: FieldProperties;
	// Frontend-only concepts
	sortable?: boolean;
	// Null means the field is not restricted i.e. available
	availability: Availability | null;
	// This is used to store the format configuration for number field
	formatConfiguration?: FormatConfigurationType | null;
};

export type LayoutItemKey = string;

export const toLayoutItemKey = (layoutItemType: LayoutItemType, dataKey: string): LayoutItemKey =>
	`${layoutItemType}.${dataKey}`;

// { ...LayoutItemOperations, ... } - inexact version of LayoutItemOperations
// to allow custom extended operations for different layout item types
export type GenericLayoutItem<
	T extends LayoutItemType,
	S,
	O extends Readonly<LayoutItemOperations>,
> = {
	/**
	 * This key is a composite of the layoutItemType and a unique identifier for the respective item. e.g. For the
	 * description field the key would be FIELD.description, the development panel would be PANEL.development.
	 * This is required to correctly identify the item for drag-n-drop operations and React rendering.
	 */
	readonly key: LayoutItemKey;
	readonly layoutItemType: T;
	readonly data: S;
	readonly operations: O;
	/**
	 * The category a layout item belongs to within the issue layout. For inactive layout items this will be `null`.
	 */
	readonly category: FieldCategory | null;
};

export type Panel = {
	key: string;
	name: string;
	operations: LayoutItemOperations;
};

export type PanelLayoutItem = GenericLayoutItem<
	typeof PANEL_LAYOUT_ITEM_TYPE,
	Panel,
	LayoutItemOperations
>;

export type ActiveFieldLayoutItem = GenericLayoutItem<
	typeof FIELD_LAYOUT_ITEM_TYPE,
	AssociatedField,
	FieldLayoutItemOperations
>;

export type InactiveFieldLayoutItem = GenericLayoutItem<
	typeof FIELD_LAYOUT_ITEM_TYPE,
	Field,
	FieldLayoutItemOperations
>;

export type ActiveLayoutItem = PanelLayoutItem | ActiveFieldLayoutItem;

/**
 * Layout items may or may not be "active" in the issue layout, i.e. appearing in the main content window (active) vs
 * appearing in the right sidebar (inactive). Items that are "inactive" may not have as much data as their active
 * counterpart, e.g. {@link Field} -> {@link AssociatedField}.
 */
export type InactiveLayoutItem = PanelLayoutItem | InactiveFieldLayoutItem;
/**
 * These inactive items can also be referred to as our base layout items. It's the minimum data we know will be
 * available for any layout item to be shown on the page.
 */
export type BaseLayoutItem = InactiveLayoutItem;

export type AvailableLayoutItemGroup = {
	groupId: Group;
	totalCount: number;
	hasNextPage: boolean;
	endCursor: string | null;
	items: ReadonlyArray<BaseLayoutItem>;
};

export type LayoutItem = PanelLayoutItem | ActiveFieldLayoutItem | InactiveFieldLayoutItem;

// Represents a group of inactive layout items to be rendered in the sidebar
export type InactiveLayoutItemGroup = {
	groupId: Group;
	items: ReadonlyArray<InactiveLayoutItem>;
};

export type LayoutItemLink = {
	readonly layoutItemType: LayoutItemType;
	// This is a unique identifier that must match with exactly one item in 'store.entities.baseLayoutItem[].key' collection
	readonly layoutItemKey: LayoutItemKey;
};

/*
 * This is almost exactly the same as the type in View component here: src/packages/project-settings/issue-type-page/src/view/tabs/view.js
 * except instead of full-sized LayoutItem objects, it only stores their IDs.
 */
export type SwiftTabWithLinks = {
	tabContainerId: string;
	name: string;
	layoutItems: ReadonlyArray<LayoutItemLink>;
};

export type FieldCategoryConfiguration = {
	/** Unique identifier. Is not shown to humans. This should match the same identifier used in other places */
	name: FieldCategory;
	/** Human-readable name */
	displayName: MessageDescriptor;
	/**
	 * A list of allowed layout items is used to determine if a particular layout item is allowed to be
	 * added to this category or not.
	 *
	 * Contains strings of the following format: "FIELD_LAYOUT_ITEM_TYPE:filter", for example:
	 *  - field:* - any field layout item
	 *  - panel:* - any panel layout item
	 *  - field:summary - only field of type summary is allowed
	 *
	 * If it's empty then all layout items are allowed.
	 *
	 */
	allowedLayoutItems: LayoutItemFilter[];
	dividerText?: MessageDescriptor;
	/**
	 * Empty state is displayed if a particular category has no fields assigned to it.
	 *
	 * Empty state text is mandatory because the UI depends on it.
	 * If you have some special reason ("This category can never ever be empty") provide empty text anyway
	 * because there will be some edge case when the category can be empty anyway that you didn't think of!
	 * Even if the empty text is "If you see this text then creating issues is broken, fix it by doing X and Y".
	 */
	emptyStateText: MessageDescriptor;
	categoryEmptyState: ComponentType<EmptyStateComponentProps>;
	whatIsThis?: WhatIsThisConfiguration;
	renderBeforeItems?: ComponentType<{}>;
	allowRequiredField: boolean;
	allowFieldAliasEditing: boolean;
	allowFieldAlias: boolean;
	// Used to override field properties per issue type. This should eventually live in the backend but the only use
	// case is the currently Email Request Type so it is temporarily associated with the FieldCategoryConfiguration
	overriddenFields?: OverriddenField[];
};

export type FieldTypeObj = {
	key: FieldType;
	name: string;
	operations: FieldOperations;
};
export type FieldTypeList = FieldTypeObj[];

export type FieldCategoryWithFields = {
	configuration: FieldCategoryConfiguration;
	layoutItems: ActiveLayoutItem[];
};

/**
 * The AllowedActions type is implemented as part of the 'DelegatedAdmin' feature. It specifies the permissions
 * a user has within a CMP project (the editor layout functionality of TMP projects are not affected in the
 * 'DelegatedAdmin' feature).
 *
 * The actions dictated by AllowedActions control the visibility and availability of certain UI elements within the layout editor.
 * Therefore, any updates to these values will affect the layout editor's functionality/appearance.
 *
 * The current implementation assumes that all necessary permission and other non-permission related evaluations (please see the 'One-Pager'
 * linked below) are done in the backend, and retrieved by the LayoutConfiguration GraphQL query that is done when rendering the editor.
 *
 * Note:
 * - This feature exclusively impacts CMP projects. TMP projects remain unaffected and will not have any 'allowedActions' that can be populated.
 * - It will be rolled out incrementally to other CMP project types (JSW, JSM) that are affected by the new 'DelegatedAdmin' features
 *
 * For more information, please see:
 * https://hello.atlassian.net/wiki/x/0orlng (One-Pager) and
 * https://hello.atlassian.net/wiki/x/XnO_ow ('What can users do?' with the new permissions)
 */
export type AllowedActions = {
	/**
	 * A Boolean value indicating if a user has the capability to search for
	 * existing fields and add them to an issue layout in the issue layout editor.
	 * This property is used by the sidebar within the editor to determine whether to show the searchbox
	 * and all available fields, including those not in the current layout displayed.
	 * TODO: Remove this field, it is replaced by accessControl https://hello.jira.atlassian.cloud/browse/DAGC-114
	 * */
	canAddNewFields: boolean;
};

/**
 * Contains a set of fields which expose what user is allowed to do with a given issue layout.
 */
export type AccessControl = {
	userHasEditLayoutPermission: boolean;
	isScreenIsolated: boolean;
};
